import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { UpgradeModule } from '@angular/upgrade/static';
import { FormlyModule } from '@ngx-formly/core';
import { AgGridModule } from 'ag-grid-angular';
import { MultilevelMenuService, NgMaterialMultilevelMenuModule } from 'ng-material-multilevel-menu';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import {
	AccountSearchComponent,
	AjsCancelPrintChecksSessionButtonComponent,
	AjsFaIconTooltipComponent,
	ApprovalHistoryComponent,
	ButtonBarComponent,
	DataSectionComponent,
	DocManagementComponent,
	ExpandCollapseActionsLinkComponent,
	FeedbackPanelComponent,
	FileUploadComponent,
	FileUploadDnDComponent,
	ImagePreviewComponent,
	KebabComponent,
	LoadSavedReportFiltersComponent,
	LoadingComponent,
	LocalUseDataEntryComponent,
	ModalComponent,
	NoDataAvailableComponent,
	NotesComponent,
	PageNotFoundComponent,
	PopoverComponent,
	SaveReportFiltersComponent,
	SavedReportFilterButtonsComponent,
	SavedReportFilterUserSelectionComponent,
	StringListAddEditComponent,
	TableComponent,
	TitleButtonsComponent,
	TitleComponent,
	VendorSearchComponent,
} from './components';
import { ApprovalHistoryService } from './components/approval-history/services/approval-history.service';
import { DocManagementService } from './components/doc-management/services/doc-management.service';
import { ErrorSummaryComponent } from './components/error-summary/error-summary.component';
import { LocalUseFieldConfigProvider } from './components/local-use-data-entry/services/local-use-field-config.provider';
import { LocalUseService } from './components/local-use-data-entry/services/local-use.service';
import { NotesService } from './components/notes/services/notes.service';
import { SavedReportFilterUserService } from './components/save-report-filter/services/saved-report-filter-user.service';
import { SavedReportFilterService } from './components/save-report-filter/services/saved-report-filter.service';
import {
	ClickToCopyDirective,
	DecimalFormatDirective,
	DuplicateValueValidatorDirective,
	ExistingDataDuplicateValueValidatorDirective,
	FileUploadDragAndDropDirective,
	HasBegEndWhitespaceDirective,
	MatAutocompleteItemSelectedDirective,
	MaxCharsValidatorDirective,
	MaxDecimalPlacesValidatorDirective,
	MaxValidatorDirective,
	MinValidatorDirective,
	NumbersOnlyDirective,
	QuestionMarkMaskDirective,
	RequiredNoWhitespaceDirective,
} from './directives';
import { NgForKeyDirective, NgForTrackByIdDirective, NgForTrackByItemDirective } from './directives/ng-for-track-by-id.directive';
import { DesignComponent, HeaderComponent, MenuComponent } from './layout';
import { MenuService } from './layout/menu/services/menu-service';
import { MaterialModule } from './material.module';
import { CapitalizePipe, EmptyPipe, LabelPipe, SafePipe } from './pipes';
import { SplitTextPipe } from './pipes/split-text.pipe';
import {
	AccountCategoryService,
	EventHubLogService,
	GlobalServerService,
	GlobalUnitService,
	InvoiceService,
	ProcessPaymentsService,
	StateExportAccountTemplateService,
	TransactionHistoryService,
	UnitListItemService,
} from './services';
import 'ag-grid-enterprise';

const components = [
	AccountSearchComponent,
	ApprovalHistoryComponent,
	ButtonBarComponent,
	DataSectionComponent,
	DesignComponent,
	DocManagementComponent,
	FeedbackPanelComponent,
	FileUploadComponent,
	FileUploadDnDComponent,
	HeaderComponent,
	KebabComponent,
	LoadingComponent,
	LocalUseDataEntryComponent,
	MenuComponent,
	ModalComponent,
	NoDataAvailableComponent,
	NotesComponent,
	PageNotFoundComponent,
	PopoverComponent,
	TableComponent,
	TitleButtonsComponent,
	TitleComponent,
	AjsFaIconTooltipComponent,
	StringListAddEditComponent,
	ExpandCollapseActionsLinkComponent,
	ErrorSummaryComponent,
	ImagePreviewComponent,
	AjsCancelPrintChecksSessionButtonComponent,
	VendorSearchComponent,
	SaveReportFiltersComponent,
	SavedReportFilterButtonsComponent,
	SavedReportFilterUserSelectionComponent,
	LoadSavedReportFiltersComponent,
];

const directives = [
	ClickToCopyDirective,
	DuplicateValueValidatorDirective,
	DecimalFormatDirective,
	ExistingDataDuplicateValueValidatorDirective,
	FileUploadDragAndDropDirective,
	HasBegEndWhitespaceDirective,
	MatAutocompleteItemSelectedDirective,
	MaxCharsValidatorDirective,
	MaxDecimalPlacesValidatorDirective,
	MaxValidatorDirective,
	MinValidatorDirective,
	NgForKeyDirective,
	NgForTrackByIdDirective,
	NgForTrackByItemDirective,
	NumbersOnlyDirective,
	QuestionMarkMaskDirective,
	RequiredNoWhitespaceDirective,
];

const services = [
	ApprovalHistoryService,
	DocManagementService,
	EventHubLogService,
	LocalUseFieldConfigProvider,
	LocalUseService,
	MenuService,
	MultilevelMenuService,
	NotesService,
	UnitListItemService,
	StateExportAccountTemplateService,
	AccountCategoryService,
	TransactionHistoryService,
	ProcessPaymentsService,
	SavedReportFilterService,
	SavedReportFilterUserService,
	InvoiceService,
	GlobalServerService,
	GlobalUnitService,
];

const pipes = [CapitalizePipe, LabelPipe, EmptyPipe, SplitTextPipe, SafePipe];

@NgModule({
	exports: [
		NgxMaskDirective,
		CommonModule,
		FormsModule,
		MaterialModule,
		ReactiveFormsModule,
		RouterModule,
		AgGridModule,
		...components,
		...directives,
		...pipes,
	],
	imports: [
		CommonModule,
		FormsModule,
		MaterialModule,
		NgMaterialMultilevelMenuModule,
		ReactiveFormsModule,
		RouterModule,
		UpgradeModule,
		FormlyModule,
		AgGridModule,
		NgxMaskDirective,
		...components,
		...directives,
		...pipes,
	],
	providers: [
		...services,
		...pipes,
		provideNgxMask({
			thousandSeparator: ',',
			decimalMarker: '.',
			dropSpecialCharacters: true,
			showMaskTyped: false,
		}),
	],
})
export class SharedModule {}
